import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  getDisabledSaveSearchActionButtonTooltip,
  shouldSaveSearchActionButtonDisabled,
} from '~/components/SearchV2/SearchV2.utils';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { api } from '~/redux/api/createApi';
import * as selectors from '~/redux/selectors';
import search from '~/redux/slices/search';
import { MODAL_EDIT_SAVED_SEARCH } from '~/types/modal.types';
import { ERROR, SUCCESS } from '~/types/toast.types';

import SaveSearchForm from '../SaveSearchForm/SaveSearchForm';

const EditSavedSearchModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const modal = useSelector(selectors.selectSearchResultModal);
  const resetModal = () => dispatch(search.actions.setModal());
  const { modalType } = modal || {};

  const [updateSavedSearch, updateSavedSearchResult] =
    api.endpoints.updateSavedSearch.useMutation();
  const {
    data,
    isError: updateSearchError,
    isSuccess: updateSearchSuccess,
    isLoading: isUpdatingSavedSearch,
  } = updateSavedSearchResult;

  const [formData, setFormData] = useState();

  useEffect(() => {
    if (updateSearchError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'EditSavedSearchModal.updateFailedMessage',
          defaultMessage: 'Edit saved search failed. Please try again.',
          description: 'Error message when updating saved search fails',
        }),
      );
    }
  }, [updateSearchError]);

  useEffect(() => {
    if (updateSearchSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'EditSavedSearchModal.updateSuccessMessage',
            defaultMessage: '{name} search has been successfully updated.',
            description:
              'Success message when saved search is updated. Example: name = "Document Name"',
          },
          { name: data?.name },
        ),
      );
    }
  }, [updateSearchSuccess]);

  return (
    <Modal
      title={intl.formatMessage({
        id: 'EditSavedSearchModal.modalTitle',
        defaultMessage: 'Edit Saved Search',
        description: 'Title for edit saved search modal',
      })}
      primaryAction={{
        disabled: shouldSaveSearchActionButtonDisabled(formData),
        text: intl.formatMessage({
          id: 'EditSavedSearchModal.updateButton',
          defaultMessage: 'Update',
          description: 'Update button text',
        }),
        tooltip: getDisabledSaveSearchActionButtonTooltip(formData, intl),
        onClick: () => {
          updateSavedSearch(formData).then(() => {
            resetModal();
          });
        },
        isLoading: isUpdatingSavedSearch,
      }}
      overflow="visible"
      isVisible={modalType === MODAL_EDIT_SAVED_SEARCH}
      onCancel={resetModal}
      onHide={resetModal}
    >
      <SaveSearchForm handleFormDataChanges={setFormData} />
    </Modal>
  );
};

export default EditSavedSearchModal;
